import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';
import { InputManager, type ActionKeyId } from '../input/InputManager';

interface InputCallbacks {
  sendStop: () => void;
  onAttack: () => void;
  onInventoryToggle: () => void;
  onPickup: () => void;
  onEquip: () => void;
  onCharacterInfoToggle: () => void;
  onDropItem: () => void;
  onPassiveTreeToggle: () => void;
  onSpellsToggle: () => void;
  onSkillsToggle: () => void;
  onSpellSlot: (slotIndex: number) => void;
  onMeditate: () => void;
  onClearTarget: () => void;
  onCycleTarget: (reverse: boolean) => void;
  onToggleMap: () => void;
  onToggleBoat: () => void;
  onCycleSummonMode: () => void;
  onUseItem: () => void;
}

interface FrameState {
  blocked: boolean; // menú abierto o reconectando
  chatFocused: boolean;
  moveSpeed: number; // multiplicador de velocidad del jugador (1.0 = normal)
}

/**
 * Sistema de input de alto nivel (estilo argentumonline.io)
 * 
 * IMPORTANTE: Este sistema NO inicia movimientos.
 * Los movimientos los inicia GameScene.tryStartLocalMovement() cada frame.
 * 
 * Este sistema solo:
 * - Detecta cuando se SUELTA la tecla para enviar STOP al servidor
 * - Maneja acciones de inventario, pickup, etc.
 */
export class InputSystem {
  private inputManager: InputManager;
  private callbacks: InputCallbacks;
  private lastDirection: Direction | null = null;
  private shiftKey: Phaser.Input.Keyboard.Key | null = null;

  constructor(inputManager: InputManager, callbacks: InputCallbacks, scene?: Phaser.Scene) {
    this.inputManager = inputManager;
    this.callbacks = callbacks;
    if (scene?.input.keyboard) {
      // Captura el SHIFT para detectar combos (Shift+Tab = cycle target reverse).
      this.shiftKey = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
    }
  }

  /**
   * Procesa el frame de input. Debe llamarse desde Scene.update.
   * 
   * NO inicia movimientos - eso lo hace GameScene.tryStartLocalMovement()
   * Solo detecta cuando se suelta la tecla para notificar al servidor.
   */
  processFrame(state: FrameState, _now: number): void {
    const { blocked, chatFocused } = state;
    if (blocked || chatFocused) {
      this.resetMovement();
      return;
    }

    const direction = this.inputManager.getDirectionFromInput();

    // Sin teclas presionadas: enviar stop una sola vez
    if (direction === null) {
      if (this.lastDirection !== null) {
        this.callbacks.sendStop();
      }
      this.lastDirection = null;
      this.handleActions();
      return;
    }

    // Actualizar lastDirection para tracking
    this.lastDirection = direction;

    this.handleActions();
  }

  /**
   * Forzar STOP_MOVE y limpiar estado (ej: al entrar en chat).
   */
  resetMovement(): void {
    if (this.lastDirection !== null) {
      this.callbacks.sendStop();
      this.lastDirection = null;
    }
    this.nextMoveAt = 0;
  }

  /**
   * Acciones discrecionales (no relacionadas al movimiento continuo).
   */
  private handleActions(): void {
    if (this.inputManager.isActionJustDown('attack')) {
      this.callbacks.onAttack();
    }
    if (this.inputManager.isActionJustDown('inventory')) {
      this.callbacks.onInventoryToggle();
    }
    if (this.inputManager.isActionJustDown('pickup')) {
      this.callbacks.onPickup();
    }
    if (this.inputManager.isActionJustDown('equip')) {
      this.callbacks.onEquip();
    }
    if (this.inputManager.isActionJustDown('characterInfo')) {
      this.callbacks.onCharacterInfoToggle();
    }
    if (this.inputManager.isActionJustDown('dropItem')) {
      this.callbacks.onDropItem();
    }
    if (this.inputManager.isActionJustDown('passiveTree')) {
      this.callbacks.onPassiveTreeToggle();
    }
    if (this.inputManager.isActionJustDown('spells')) {
      this.callbacks.onSpellsToggle();
    }
    if (this.inputManager.isActionJustDown('skills')) {
      this.callbacks.onSkillsToggle();
    }
    // Slots de hechizo 1..10 (teclas 1-9,0 por default) → castSlot(0..9).
    for (let i = 0; i < 10; i++) {
      const actionId = `spell${i + 1}` as ActionKeyId;
      if (this.inputManager.isActionJustDown(actionId)) {
        this.callbacks.onSpellSlot(i);
      }
    }
    if (this.inputManager.isActionJustDown('meditate')) {
      this.callbacks.onMeditate();
    }
    if (this.inputManager.isActionJustDown('clearTarget')) {
      this.callbacks.onClearTarget();
    }
    if (this.inputManager.isActionJustDown('cycleTarget')) {
      const reverse = this.shiftKey?.isDown ?? false;
      this.callbacks.onCycleTarget(reverse);
    }
    if (this.inputManager.isActionJustDown('toggleMap')) {
      this.callbacks.onToggleMap();
    }
    if (this.inputManager.isActionJustDown('toggleBoat')) {
      this.callbacks.onToggleBoat();
    }
    if (this.inputManager.isActionJustDown('cycleSummonMode')) {
      this.callbacks.onCycleSummonMode();
    }
    if (this.inputManager.isActionJustDown('useItem')) {
      this.callbacks.onUseItem();
    }
  }
}





